/**
 * Normalize xuanfei list rows to card models using API fields (no local fake names/cities).
 */

/**
 * 列表/详情「ID编号」：只用接口 xuanfei_code（8 位纯数字，可含前导 0）。
 * 勿与自增 id 混用。JSON 若把编号当成 number，这里补回前导 0。
 */
export function resolveProfileDisplayId(item) {
  if (!item) {
    return "";
  }
  const candidates = [item.xuanfei_code, item.xuanfeiCode];
  for (let i = 0; i < candidates.length; i += 1) {
    if (candidates[i] == null || candidates[i] === "") {
      continue;
    }
    const text = String(candidates[i]).trim();
    if (!text) {
      continue;
    }
    if (/^\d{1,8}$/.test(text)) {
      return text.padStart(8, "0");
    }
    return text;
  }
  return "";
}

export function resolveAddressLabel(address, lang) {
  if (!address) {
    return "";
  }
  const code = lang || "zh_cn";
  return (
    address[code] ||
    address.zh_cn ||
    address.name ||
    address.en_us ||
    ""
  );
}

export function buildAddressMap(list, lang) {
  const map = {};
  (Array.isArray(list) ? list : []).forEach((item) => {
    const id = Number(item && item.id);
    if (!Number.isFinite(id)) {
      return;
    }
    map[id] = resolveAddressLabel(item, lang);
  });
  return map;
}

/** Normalize backend address rows into home nearby-area tabs. */
export function buildAddressTabs(list, lang) {
  return (Array.isArray(list) ? list : [])
    .map((item) => {
      const id = Number(item && item.id);
      if (!Number.isFinite(id)) {
        return null;
      }
      const label = resolveAddressLabel(item, lang);
      if (!label) {
        return null;
      }
      return {
        id,
        key: `area-${id}`,
        label,
      };
    })
    .filter(Boolean);
}

/** Backend multilang columns for 介绍 (admin: zh_cn / zh_tw / en_us / yn_yu ...) */
const INTRO_LANG_FIELDS = [
  "zh_cn",
  "zh_tw",
  "ms_my",
  "en_us",
  "es_spa",
  "yn_yu",
  "idn_yu",
  "ko_hy",
];

function normalizeLangCode(lang) {
  const raw = String(lang || "zh_cn").toLowerCase();
  const aliases = {
    "zh-cn": "zh_cn",
    zh: "zh_cn",
    "zh-tw": "zh_tw",
    tw: "zh_tw",
    zh_hk: "zh_tw",
    "zh-hk": "zh_tw",
    en: "en_us",
    "en-us": "en_us",
    vi: "yn_yu",
    vn: "yn_yu",
    ko: "ko_hy",
    ms: "ms_my",
    id: "idn_yu",
    es: "es_spa",
  };
  return aliases[raw] || raw;
}

/**
 * 介绍字段语序：当前语言优先，缺省回退简体 desc_zh_cn / zh_cn。
 */
function introFieldKeysForLang(lang) {
  const code = normalizeLangCode(lang);
  if (code === "zh_tw") {
    return ["zh_tw", "zh_cn"];
  }
  if (code === "zh_cn") {
    return ["zh_cn", "zh_tw"];
  }
  if (INTRO_LANG_FIELDS.indexOf(code) !== -1) {
    return [code, "zh_cn"];
  }
  return ["zh_cn"];
}

function pickPlainText(value) {
  if (value == null || typeof value === "object") {
    return "";
  }
  return String(value).trim();
}

/**
 * 介绍：只用 desc / desc_*（zh_cn、en_us 等是多语言姓名，不能当介绍）。
 * 优先 Header lang 对应字段，缺省回退简体。
 */
export function resolveAbout(item, lang) {
  if (!item) {
    return "";
  }
  const code = normalizeLangCode(lang);
  const candidates = [];

  // 1) desc：字符串（后台已按 lang 取好）或对象 { zh_cn, en_us, ... }
  if (item.desc != null) {
    if (typeof item.desc === "object" && !Array.isArray(item.desc)) {
      candidates.push(item.desc[code], item.desc.zh_cn, item.desc.zh_tw);
    } else {
      candidates.push(item.desc);
    }
  }

  // 2) 仅 desc_zh_cn / desc_en_us ...，不要回退到 zh_cn（那是名字）
  const keys = introFieldKeysForLang(lang);
  for (let i = 0; i < keys.length; i += 1) {
    candidates.push(item[`desc_${keys[i]}`]);
  }

  for (let i = 0; i < candidates.length; i += 1) {
    const text = pickPlainText(candidates[i]);
    if (text) {
      return text;
    }
  }
  return "";
}

/** 列表「年龄」：优先 age，后台常把年龄写在 height */
export function resolveAge(item) {
  if (!item) {
    return "";
  }
  const raw = item.age != null && item.age !== "" ? item.age : item.height;
  if (raw == null || raw === "") {
    return "";
  }
  return String(raw).trim();
}

function resolveRole(item, copy) {
  const age = resolveAge(item);
  if (age) {
    return age;
  }
  const bust = item && item.bust ? String(item.bust).trim() : "";
  if (bust) {
    return bust;
  }
  const star = Number(item && item.fee_star);
  if (star > 0) {
    return `${star}${(copy && copy.starUnit) || "★"}`;
  }
  return "";
}

export function normalizeXuanfeiProfile(item, index, options = {}) {
  const copy = options.copy || {};
  const addressMap = options.addressMap || {};
  const lang = options.lang || "zh_cn";
  const resolveAssetUrl = options.resolveAssetUrl || ((url) => url || "");

  const id = item.id || index + 1;
  const hot = Number(item.is_hot) === 1;
  const online = hot || Number(item.is_recommend) === 1 || index % 4 !== 0;
  const classId = Number(item.class_id || 0);
  const city =
    (classId && addressMap[classId]) ||
    item.address_name ||
    item.city ||
    item.area ||
    "";
  const name = String(item.xuanfei_name || item.name || "").trim();
  // 介绍只用 desc；若后台误把姓名写入 desc，也避免列表「介绍」展示成名字
  let about = resolveAbout(item, lang) || "";
  if (about && name && about === name) {
    about = "";
  }
  const image = resolveAssetUrl(item.img_url) || item.img_url || "";
  const onlineMin = Number(item.online_min);
  const onlineMax = Number(item.online_max);
  const feeStar = Number(item.fee_star);
  const age = resolveAge(item);
  // ID编号 = xuanfei_code（接口原样，含前导 0；勿用自增 id）
  const publicId = resolveProfileDisplayId(item);

  return {
    id,
    xuanfeiCode: publicId,
    image: image || "/img/null.png",
    name,
    age,
    role: age || resolveRole(item, copy),
    city,
    publicId,
    about,
    tag: online ? copy.bookable || "" : copy.busy || "",
    online,
    hot,
    certified: true,
    isNew: Number(item.is_recommend) === 1 || index < 4 || Number(item.create_time || 0) > 0,
    group: Number(item.is_ranking) === 1 ? "nearby" : index % 2 === 0 ? "nearby" : "remote",
    classId,
    onlineMin: Number.isFinite(onlineMin) ? Math.floor(onlineMin) : null,
    onlineMax: Number.isFinite(onlineMax) ? Math.floor(onlineMax) : null,
    feeStar: Number.isFinite(feeStar) ? Math.max(0, Math.min(5, Math.floor(feeStar))) : 0,
    raw: item,
  };
}
